import React, { useCallback, useMemo, useState } from 'react'
import { useGetCustomerSourcesByDealershipID } from '../../../../api/dealership/customers/sources/useGetCustomerSourcesByDealershipID'
import { motion } from 'framer-motion'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import { useModal } from '../../../../components/useModal'
import { ReactComponent as ActionDelete } from '../../../../images/icons/action_delete.svg'
import { ReactComponent as ActionEdit } from '../../../../images/icons/action_edit.svg'
import DeleteCustomerSource from '../../../../forms/CustomersSources/Sources/DeleteSource'
import EditCustomerSource from '../../../../forms/CustomersSources/Sources/EditSource'
import toast from 'react-hot-toast'
import { VENDOR } from '../../../../lib/utils/vendors'
import {
  ActionRefresh,
  DateRangePicker,
  SelectInput,
  TextInput,
  Button,
  Table,
  Pill,
  Info,
  StatsCard,
} from '@demandlocal/components'
import { useRunSourceFetch } from '../../../../api/dealership/customers/sources/useRunSourceFetch'
import { createColumnHelper } from '@tanstack/react-table'
import { CustomerSource } from '../../../../lib/types/CustomerSource'
import { OutPortal, createHtmlPortalNode } from 'react-reverse-portal'
import { ReactComponent as ActionChart } from '../../../../images/icons/sync_primary.svg'
import { useGetCustomerSourceMetrics } from '../../../../api/dealership/customers/sources/useGetCustomerSourceMetrics'
import dayjs from 'dayjs'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import MultiLineChart from '../../../../components/Charts/MultiLineChart'
import utc from 'dayjs/plugin/utc'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { GA4 } from '../../../../lib/utils/ga4'
import mtm from '../../../../lib/utils/mtm'
import { match } from 'ts-pattern'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useGetOverallStats } from '../../../../api/dealership/useGetOverallStats'
import { useGetThirdPartyAudiences } from '../../../../api/dealership/customers/audiences/useGetThirdPartyAudiences'
import { useGetAgencyByAgencyId } from '../../../../api/agencies/useGetAgencyByAgencyId'
dayjs.extend(utc)

// eslint-disable-next-line no-restricted-syntax
enum SourceType {
  ftp = 1,
  manual = 2,
  third_party = 3,
  secure_link = 4,
}

type FilterType = {
  customer_source: string
  customer_type: string
}

type DateRange = {
  dateRange: {
    startDate: string
    endDate: string
  }
}

const columnHelper = createColumnHelper<CustomerSource>()

const CustomersSources = (): JSX.Element => {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const navigate = useNavigate()
  const [filters, setFilters] = useState<Partial<FilterType>>({})
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [chartFilters, setChartFilters] = useState<DateRange>({
    dateRange: {
      startDate: dayjs().utc().subtract(31, 'days').format('YYYY-MM-DD'),
      endDate: dayjs().utc().format('YYYY-MM-DD'),
    },
  })
  const [chartSource, setChartSource] = useState<number | null>(null)

  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const isOmni = useMemo(() => dealership?.organization_id === 112, [dealership])
  const {
    data: {
      totalCustomers = 0,
      totalAudiences = 0,
      sources = {
        total: 0,
      },
    } = {},
    isLoading: isLoadingOverallStats,
  } = useGetOverallStats()

  const { data: thirdPartyAudiences, isLoading: isLoadingThirdPartyAudiences } =
    useGetThirdPartyAudiences({
      pageSize: 1,
      sorting: [{ id: 'vendor_audience_details.lastUpdatedAtUTC', desc: true }],
    })

  const latestAudience = useMemo(() => {
    if (
      !isLoadingThirdPartyAudiences &&
      thirdPartyAudiences?.pagination &&
      thirdPartyAudiences?.pagination?.total &&
      thirdPartyAudiences?.pagination?.total > 0
    ) {
      return {
        activeIds: { total: 0 },
        personsCount: 0,
        householdCount: 0,
      }
    } else {
      return {
        activeIds: { total: 0 },
        personsCount: 0,
        householdCount: 0,
      }
    }
  }, [thirdPartyAudiences, isLoadingThirdPartyAudiences])

  const { data: metrics, isLoading: isLoadingMetrics } = useGetCustomerSourceMetrics({
    id: chartSource ?? 0,
    filters: {
      start_date: chartFilters.dateRange.startDate,
      end_date: chartFilters.dateRange.endDate,
    },
  })

  const { data, refetch } = useGetCustomerSourcesByDealershipID({})
  const { handleModal } = useModal()
  const { mutate: fetchSource } = useRunSourceFetch()
  const triggerSourceRefetch = useCallback(
    (customer_source_id: number) => {
      const toastId = toast.loading('Refreshing source...')
      fetchSource(
        { customer_source_id },
        {
          onSuccess: () => {
            toast.success('Source is pulling data in the background.', { id: toastId })
            refetch()
          },
        },
      )
    },
    [fetchSource, refetch],
  )

  const headerCards = useMemo(() => {
    return isOmni
      ? [
          {
            title: 'Total Active IDs',
            content: formatNumber(latestAudience?.activeIds.total),
          },
          {
            title: () => (
              <div className="flex flex-row gap-2">
                <span>Total Unique Customers</span>
                <Info
                  message={
                    'Customers that are unique after processing and de-duplication from all of your customer sources'
                  }></Info>
              </div>
            ),
            content: formatNumber(latestAudience?.personsCount),
          },
          {
            title: 'Total Households',
            content: formatNumber(latestAudience?.householdCount),
          },
        ]
      : [
          {
            title: (
              <div className="flex flex-row gap-2">
                <span>Total Unique Customers</span>
                <Info
                  message={
                    'Customers that are unique after processing and de-duplication from all of your customer sources'
                  }></Info>
              </div>
            ),
            content: formatNumber(totalCustomers),
          },
          {
            title: 'Total Sources',
            content: formatNumber(sources?.total),
          },
          {
            title: 'Total Audiences',
            content: formatNumber(totalAudiences),
          },
        ]
  }, [isOmni, latestAudience, sources?.total, totalAudiences, totalCustomers])

  const onDateRangeChange = (dateRange: DateRange): void => {
    setChartFilters(dateRange)
  }

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'status',
        header: () => 'Status',
        enableSorting: false,
        cell: (info) => {
          const row = info.row
          const status = match(row.original.run_status)
            .with('available', () => ({
              text: 'daily',
              type: 'info-purple',
            }))
            .with('locked', () => ({
              text: 'loading',
              type: 'warning',
            }))
            .with('imported', () => ({
              text: 'imported',
              type: 'success',
            }))
            .with('done', () => ({
              text: 'imported',
              type: 'success',
            }))
            .otherwise(() => ({
              text: 'unknown',
              type: 'info',
            }))
          return (
            <Pill
              type={
                status.type as
                  | 'warning'
                  | 'error'
                  | 'success'
                  | 'info'
                  | 'info-purple'
                  | 'gray'
                  | 'rainbow'
              }>
              {status.text}
            </Pill>
          )
        },
      }),
      columnHelper.accessor('customer_source', {
        header: () => 'Source Name',
        cell: ({ row }) => {
          if (row.original.feed_status === 'pending')
            return `${row.original.customer_source} (Pending approval)`

          return (
            <Link
              to={`../details/${row.original.id}`}
              className="text-primary-persian-blue font-bold"
              onClick={() => {
                GA4.sendEvent({
                  event_name: 'view_source_report',
                  event_params: { customer_source_id: row.original.id },
                })
                mtm.sendEvent('View', 'Source', 'Report', row.original.id)
              }}>
              {row.original.customer_source}
            </Link>
          )
        },
      }),
      columnHelper.display({
        id: 'connection_source',
        header: () => 'Connection Source',
        enableSorting: false,
        cell: (info) => {
          const row = info.row
          if (row.original.data_provider_id === 3) {
            if (row.original.vendor_id === VENDOR.DemandLocal)
              return 'Demand Local Marketing'

            return row.original.vendor_id
              ? String(VENDOR[row.original.vendor_id]).replace(/([a-z](?=[A-Z]))/g, '$1 ')
              : 'Unknown'
          }
          if (row.original.data_provider_id === 1) return 'FTP Upload'
          if (row.original.data_provider_id === 2) return 'Manual Upload'
          if (row.original.data_provider_id === 4) return 'Secure Link'
          return 'Unknown'
        },
      }),
      columnHelper.accessor('saas_customer_source_type', {
        header: () => 'Source Type',
        cell: (info) => {
          const value = info?.getValue()
          if (value === 1) return 'Lead Customers List'
          if (value === 2) return 'Sold Report List'
          if (value === 3) return 'Service Customers List'
          if (value === 4) return 'Customers List'
          if (value === 5) return 'Hashed Customers List'
          if (value === 6) return 'Opt Out Customers List'
          return ''
        },
      }),
      columnHelper.accessor('last_run', {
        header: () => 'Last update',
        enableSorting: true,
        cell: (info) => {
          const row = info.row
          return row.original.last_run
            ? dayjs(row.original.last_run).format('MMM DD, YYYY')
            : '-'
        },
      }),
      columnHelper.accessor('uploaded_rows', {
        header: () => (
          <div className={'flex flex-row gap-2 items-center justify-between'}>
            <p>Initial Upload</p>
            <Info message={'Initial amount of customers this source started with'} />
          </div>
        ),
        enableSorting: false,
        cell: (info) => info.getValue(),
      }),
      columnHelper.display({
        id: 'customers_added',
        header: () => (
          <div className={'flex flex-row gap-2 items-center justify-between'}>
            <p>Unique Customers</p>
            <Info
              message={'Customers from this source after processing and de-duplication'}
            />
          </div>
        ),
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className={'flex flex-row gap-1'}>
              <span>{row.original.customers_added}</span>
            </div>
          )
        },
      }),
      columnHelper.display({
        id: 'table-actions',
        enableSorting: false,
        header: () => 'Actions',
        cell: (info) => {
          const row = info.row
          const disabled = row.original.run_status === 'locked'
          return (
            <div className={'flex flex-row gap-4'}>
              <ActionDelete
                className={'cursor-pointer'}
                onClick={(e) => {
                  e.preventDefault()
                  handleModal(<DeleteCustomerSource source={row.original} />)
                }}
              />
              <ActionEdit
                stroke={
                  !(
                    row.original.saas_data_provider_id === 4 ||
                    row.original.vendor_id === VENDOR.DemandLocal
                  )
                    ? '#0932C2'
                    : '#CCC'
                }
                className={`${
                  !(
                    row.original.saas_data_provider_id === 4 ||
                    row.original.vendor_id === VENDOR.DemandLocal
                  )
                    ? 'cursor-pointer'
                    : ''
                }`}
                onClick={(e) => {
                  e.preventDefault()
                  const source_type = SourceType[row.original.data_provider_id] as 'ftp'
                  if (
                    row.original.saas_data_provider_id !== 4 &&
                    row.original.vendor_id !== VENDOR.DemandLocal
                  ) {
                    handleModal(
                      <EditCustomerSource
                        customer_source_id={row.original.id}
                        source_type={source_type}
                      />,
                    )
                  }
                }}
              />
              <ActionRefresh
                // @ts-ignore
                className={`cursor-pointer  ${
                  !(
                    row.original.data_provider_id === 2 ||
                    row.original.data_provider_id === 4 ||
                    disabled
                  )
                    ? 'stroke-primary-persian-blue  fill-primary-persian-blue'
                    : 'stroke-web-gray  fill-web-gray'
                }`}
                onClick={() => {
                  if (
                    !(
                      row.original.data_provider_id === 2 ||
                      row.original.data_provider_id === 4 ||
                      disabled
                    )
                  )
                    triggerSourceRefetch(row.original.id)
                }}
              />
              <ActionChart
                className={
                  'cursor-pointer stroke-primary-persian-blue fill-primary-persian-blue'
                }
                onClick={() => setChartSource(row.original.id)}
              />
            </div>
          )
        },
      }),
    ],
    [handleModal, triggerSourceRefetch, setChartSource],
  )

  return (
    <motion.div className={'flex flex-1 flex-col pb-8 px-10 font-Spartan'}>
      <div className={'flex flex-1 flex-col'}>
        <span className="text-md pb-5">
          Connect and manage customer data sources for enhanced aggregation and audience
          building. Add a new source to pull in more customers. It is important to import
          your CRM and DMS for some reporting.
        </span>
        <div className="flex flex-row gap-3 pb-5">
          {headerCards.map((h, i) => (
            <StatsCard key={i} title={h.title} content={h.content} />
          ))}
        </div>
        <div className={'pb-4 flex justify-between items-center'}>
          <div className="flex flex-row gap-2 items-center">
            <span className="font-bold text-lg">Sources</span>
            <div
              className={
                'flex font-Spartan font-bold text-white p-2 rounded-xl text-[10px] bg-primary-xiketic'
              }>
              {formatNumber(data?.pagination?.total)}
            </div>
          </div>
          <div className={'flex flex-row items-center gap-3'}>
            <DateRangePicker filters={{ ...chartFilters }} onChange={onDateRangeChange} />
            <div></div>
            {/* Portal Crashes without static div before it to be inserted after */}
            <OutPortal
              node={filtersPortalNode}
              className={'flex flex-row border-r-2 pr-2 gap-1'}
            />
            <Button
              onClick={() => {
                navigate(t('dealership.customers.sources.create', { dealership }))
              }}>
              + Add New Source
            </Button>
          </div>
        </div>
        <FiltersContext portalNode={filtersPortalNode} setFilters={setFilters}>
          <TextInput
            name={'customer_source'}
            placeholder={'Name'}
            label={'Source name'}
          />
          <SelectInput
            name={'source_type'}
            label={'Source Type'}
            options={[
              { label: 'All sources', value: -1 },
              { label: 'Customers list', value: 1 },
              { label: 'Sold report', value: 2 },
            ]}
          />
        </FiltersContext>

        <div className="pb-5">
          <div className={'flex grid gap-5 py-2'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <div className={'flex flex-row gap-1 justify-center items-center'}>
                  <div
                    className={classNames(
                      'bg-primary-persian-blue/10 rounded-full flex justify-center items-center w-10 h-10',
                      {
                        'opacity-50': !metrics || metrics.length === 0,
                      },
                    )}>
                    <ActionChart
                      className={'stroke-primary-persian-blue fill-primary-persian-blue'}
                    />
                  </div>
                  <span
                    className={classNames('pl-3', {
                      'opacity-50': !metrics || metrics.length === 0,
                    })}>
                    Customer Count
                  </span>
                </div>
              </div>
              <div className={'h-[270px] p-2'}>
                <MultiLineChart
                  data={metrics ?? []}
                  gradient={['blue-to-purple', 'red-to-orange']}
                  borderColor="black"
                  groupKey={'customer_source_id'}
                  labelKey={'customer_source'}
                  yKey={'customersSize'}
                  date_range={chartFilters}
                  isLoading={isLoadingMetrics}
                  isPreviousData={false}
                />
              </div>
            </div>
          </div>
        </div>
        <Table
          columns={columns}
          filters={filters}
          defaultSorting={[{ id: 'last_run', desc: true }]}
          fetchFn={useGetCustomerSourcesByDealershipID}
        />
      </div>
      <Outlet />
    </motion.div>
  )
}

export default CustomersSources
